import { useSyncExternalStore } from 'react'
import {
  canTransition,
  newSession,
  readHunt,
  type Evidence,
  type Finding,
  type Hunt,
  type HuntSession,
  type TrailStep,
  type Verdict,
} from './hunt-model'

/**
 * Hunt persistence — localStorage v1 (decision H8), behind an interface the
 * future hunt API implements without the UI noticing. Everything the UI does
 * goes through HuntStore; nothing else touches the storage key.
 *
 * THIS INTERFACE IS THE DRAFT API CONTRACT. When the backend lands (needs-API
 * ledger item 1), each method becomes an endpoint and LocalHuntStore retires.
 *
 * Storage: ONE key per cluster+scope, `bloo.hunt.<clusterId>.<scopeId>`,
 * holding a JSON array. House localStorage rules apply (cf. search-history):
 * try-catch everywhere, SSR-safe, corrupt records quarantined by the tolerant
 * reader rather than crashing the page.
 */

export interface HuntStore {
  list: () => Hunt[]
  get: (id: string) => Hunt | null
  create: (hunt: Hunt) => void
  /** Shallow-merges the patch; bumps updatedAt. No-op when the id is unknown. */
  update: (id: string, patch: Partial<Omit<Hunt, 'id' | 'schema'>>) => void
  remove: (id: string) => void

  /** Starts a new sitting; returns it so the caller can target steps at it. */
  startSession: (huntId: string, title: string) => HuntSession | null
  endSession: (huntId: string, sessionId: string) => void
  appendTrailStep: (huntId: string, sessionId: string, step: TrailStep) => void
  curateStep: (
    huntId: string,
    sessionId: string,
    stepId: string,
    patch: Partial<Pick<TrailStep, 'starred' | 'pruned' | 'note'>>,
  ) => void

  pinEvidence: (huntId: string, evidence: Evidence) => void
  removeEvidence: (huntId: string, evidenceId: string) => void
  addFinding: (huntId: string, finding: Finding) => void
  removeFinding: (huntId: string, findingId: string) => void
  /**
   * Marks a finding escalated-to-case. Cases have no API yet — the case
   * bundle stays derivable from the hunt (buildCaseBundle) until it does.
   */
  escalateFinding: (huntId: string, findingId: string) => void
  /** Replaces the hunt's pivot graph (E5) — the graph lives with the case. */
  setGraph: (huntId: string, graph: import('./hunt-graph').HuntGraph) => void

  /** active -> closed, verdict required (the status machine enforces it). */
  close: (huntId: string, verdict: Verdict, note: string) => void
  /** closed -> active. The old verdict stays until the next close rewrites it. */
  reopen: (huntId: string) => void
  /** draft -> active — first run does this implicitly. */
  activate: (huntId: string) => void

  /** True when the last write was refused or trimmed for space. */
  quotaWarning: () => string | null
  subscribe: (cb: () => void) => () => void
}

export function huntStorageKey(clusterId: string, scopeId: string): string {
  return `bloo.hunt.${clusterId}.${scopeId}`
}

/**
 * Guard rails for the 5MB origin quota: an excerpt field is capped, an
 * excerpt keeps at most this many fields, and a store past the soft budget
 * refuses new EVIDENCE (the bulky kind) with a visible warning instead of
 * dying on the write.
 */
export const EXCERPT_VALUE_MAX = 500
export const EXCERPT_FIELDS_MAX = 12
export const STORE_SOFT_BUDGET_BYTES = 2_000_000

/** Trim an evidence excerpt to the quota rules — exported for tests. */
export function trimExcerpt(excerpt: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  let n = 0
  for (const [k, v] of Object.entries(excerpt)) {
    if (n >= EXCERPT_FIELDS_MAX) break
    out[k] = v.length > EXCERPT_VALUE_MAX ? `${v.slice(0, EXCERPT_VALUE_MAX)}…` : v
    n++
  }
  return out
}

/** One store (and one snapshot cache) per storage key, process-wide. */
const stores = new Map<string, LocalHuntStore>()

export function localHuntStore(clusterId: string, scopeId: string): HuntStore {
  const key = huntStorageKey(clusterId, scopeId)
  let s = stores.get(key)
  if (!s) {
    s = new LocalHuntStore(key)
    stores.set(key, s)
  }
  return s
}

class LocalHuntStore implements HuntStore {
  private key: string
  /** Stable snapshot for useSyncExternalStore — replaced only on mutation. */
  private cache: Hunt[] | null = null
  private warning: string | null = null
  private listeners = new Set<() => void>()

  constructor(key: string) {
    this.key = key
  }

  /* ------------------------------------------------------------- plumbing */

  private read(): Hunt[] {
    if (this.cache) return this.cache
    let parsed: unknown
    try {
      const raw = globalThis.localStorage?.getItem(this.key)
      parsed = raw ? JSON.parse(raw) : []
    } catch {
      // Unreadable JSON or no storage: start empty. The next write replaces
      // the corrupt blob — quarantine by supersession.
      parsed = []
    }
    const hunts = (Array.isArray(parsed) ? parsed : [])
      .map(readHunt)
      .filter((h): h is Hunt => h !== null)
    this.cache = hunts
    return hunts
  }

  private write(next: Hunt[], opts?: { bulky?: boolean }): boolean {
    const body = JSON.stringify(next)
    if (opts?.bulky && body.length > STORE_SOFT_BUDGET_BYTES) {
      this.warning =
        'hunt storage is near its browser quota — export finished hunts and delete them, or unpin heavy evidence'
      this.notify()
      return false
    }
    try {
      globalThis.localStorage?.setItem(this.key, body)
      this.cache = next
      this.warning = null
      this.notify()
      return true
    } catch {
      // The browser refused the write (hard quota). Keep the in-memory state
      // untouched so the UI still shows what IS stored.
      this.warning =
        'the browser refused to save — hunt storage is full; export and delete old hunts'
      this.notify()
      return false
    }
  }

  private mutate(id: string, fn: (h: Hunt) => Hunt, opts?: { bulky?: boolean }): void {
    const hunts = this.read()
    const i = hunts.findIndex((h) => h.id === id)
    if (i === -1) return
    const next = [...hunts]
    next[i] = { ...fn(hunts[i]!), updatedAt: Date.now() }
    this.write(next, opts)
  }

  private notify() {
    for (const cb of this.listeners) cb()
  }

  subscribe = (cb: () => void) => {
    this.listeners.add(cb)
    return () => {
      this.listeners.delete(cb)
    }
  }

  quotaWarning = () => this.warning

  /* ----------------------------------------------------------------- CRUD */

  list = () => this.read()
  get = (id: string) => this.read().find((h) => h.id === id) ?? null

  create = (hunt: Hunt) => {
    this.write([hunt, ...this.read()])
  }

  update = (id: string, patch: Partial<Omit<Hunt, 'id' | 'schema'>>) => {
    this.mutate(id, (h) => ({ ...h, ...patch }))
  }

  remove = (id: string) => {
    this.write(this.read().filter((h) => h.id !== id))
  }

  /* -------------------------------------------------------------- sessions */

  startSession = (huntId: string, title: string): HuntSession | null => {
    if (!this.get(huntId)) return null
    const session = newSession(title)
    this.mutate(huntId, (h) => ({ ...h, sessions: [...h.sessions, session] }))
    return session
  }

  endSession = (huntId: string, sessionId: string) => {
    this.mutate(huntId, (h) => ({
      ...h,
      sessions: h.sessions.map((s) => (s.id === sessionId ? { ...s, endedAt: Date.now() } : s)),
    }))
  }

  appendTrailStep = (huntId: string, sessionId: string, step: TrailStep) => {
    this.mutate(huntId, (h) => ({
      ...h,
      sessions: h.sessions.map((s) =>
        s.id === sessionId ? { ...s, steps: [...s.steps, step] } : s,
      ),
    }))
  }

  curateStep = (
    huntId: string,
    sessionId: string,
    stepId: string,
    patch: Partial<Pick<TrailStep, 'starred' | 'pruned' | 'note'>>,
  ) => {
    this.mutate(huntId, (h) => ({
      ...h,
      sessions: h.sessions.map((s) =>
        s.id === sessionId
          ? { ...s, steps: s.steps.map((t) => (t.id === stepId ? { ...t, ...patch } : t)) }
          : s,
      ),
    }))
  }

  /* -------------------------------------------------------------- evidence */

  pinEvidence = (huntId: string, evidence: Evidence) => {
    const trimmed = evidence.excerpt
      ? { ...evidence, excerpt: trimExcerpt(evidence.excerpt) }
      : evidence
    this.mutate(huntId, (h) => ({ ...h, evidence: [...h.evidence, trimmed] }), { bulky: true })
  }

  removeEvidence = (huntId: string, evidenceId: string) => {
    this.mutate(huntId, (h) => ({
      ...h,
      evidence: h.evidence.filter((e) => e.id !== evidenceId),
      // A finding keeps working when its evidence goes — the link just drops.
      findings: h.findings.map((f) => ({
        ...f,
        evidenceIds: f.evidenceIds.filter((id) => id !== evidenceId),
      })),
    }))
  }

  addFinding = (huntId: string, finding: Finding) => {
    this.mutate(huntId, (h) => ({ ...h, findings: [...h.findings, finding] }))
  }

  removeFinding = (huntId: string, findingId: string) => {
    this.mutate(huntId, (h) => ({ ...h, findings: h.findings.filter((f) => f.id !== findingId) }))
  }

  setGraph = (huntId: string, graph: import('./hunt-graph').HuntGraph) => {
    this.mutate(huntId, (h) => ({ ...h, graph }), { bulky: true })
  }

  escalateFinding = (huntId: string, findingId: string) => {
    this.mutate(huntId, (h) => ({
      ...h,
      findings: h.findings.map((f) =>
        f.id === findingId && f.escalatedAt === undefined ? { ...f, escalatedAt: Date.now() } : f,
      ),
    }))
  }

  /* -------------------------------------------------------------- lifecycle */

  activate = (huntId: string) => {
    this.mutate(huntId, (h) => (canTransition(h.status, 'active') ? { ...h, status: 'active' } : h))
  }

  close = (huntId: string, verdict: Verdict, note: string) => {
    this.mutate(huntId, (h) =>
      canTransition(h.status, 'closed')
        ? { ...h, status: 'closed', verdict, verdictNote: note, closedAt: Date.now() }
        : h,
    )
  }

  reopen = (huntId: string) => {
    this.mutate(huntId, (h) =>
      h.status === 'closed' && canTransition(h.status, 'active')
        ? { ...h, status: 'active', closedAt: null }
        : h,
    )
  }
}

/* ----------------------------------------------------------------- React */

/** Live hunt list for a scope — re-renders on every store mutation. */
export function useHunts(clusterId: string, scopeId: string): { hunts: Hunt[]; store: HuntStore } {
  const store = localHuntStore(clusterId, scopeId)
  const hunts = useSyncExternalStore(store.subscribe, store.list, store.list)
  return { hunts, store }
}

/** One hunt by id, live. Null when it does not exist (deleted, bad link). */
export function useHunt(
  clusterId: string,
  scopeId: string,
  huntId: string,
): { hunt: Hunt | null; store: HuntStore } {
  const { hunts, store } = useHunts(clusterId, scopeId)
  return { hunt: hunts.find((h) => h.id === huntId) ?? null, store }
}
